import React, { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api, { API_BASE_URL } from './api';

const MENU = {
  opponent: 'opponent',
  info: 'info',
};

const stakeOptions = [
  { value: 5, label: 'Quick Spark', blurb: 'Tiny stake for first-time challengers.' },
  { value: 10, label: 'Pocket Match', blurb: 'Low-risk match for casual rounds.' },
  { value: 25, label: 'Warm Table', blurb: 'Small table with a little pressure.' },
  { value: 50, label: 'Starter Duel', blurb: 'Light match, quick warm-up.' },
  { value: 100, label: 'Classic Table', blurb: 'Balanced stake for regular play.' },
  { value: 250, label: 'Sharp Edge', blurb: 'Higher pressure, better swing.' },
  { value: 500, label: 'Gold Room', blurb: 'Serious table for confident players.' },
  { value: 1000, label: 'High Roller', blurb: 'Big risk match with strong payoff.' },
];
const targetWinOptions = [1, 3, 5, 7];
const FAKE_PLAYER_REFRESH_MS = 30 * 1000;
const FAKE_PLAYER_BASE_COUNT = 18;
const FAKE_PLAYER_NAMES = [
  'Aiden', 'Alex', 'Alec', 'Amos', 'Andre', 'Andy', 'Asher', 'Austin', 'Barry', 'Beck',
  'Ben', 'Blake', 'Brad', 'Brent', 'Brett', 'Brian', 'Bryan', 'Caleb', 'Carl', 'Carter',
  'Chad', 'Chris', 'Clark', 'Cody', 'Colin', 'Cole', 'Craig', 'Damon', 'Dane', 'Danny',
  'Darren', 'Dean', 'Derek', 'Devon', 'Dylan', 'Eddie', 'Eli', 'Ethan', 'Evan', 'Felix',
  'Finn', 'Gabe', 'Gavin', 'Grant', 'Greg', 'Harry', 'Hayes', 'Henry', 'Hugo', 'Ian',
  'Isaac', 'Jack', 'Jake', 'James', 'Jason', 'Jesse', 'Joel', 'John', 'Jonah', 'Jordan',
  'Josh', 'Jude', 'Julian', 'Kai', 'Keith', 'Kevin', 'Kyle', 'Lance', 'Leo', 'Liam',
  'Logan', 'Louis', 'Luke', 'Marcus', 'Mark', 'Mason', 'Max', 'Milo', 'Nate', 'Neil',
  'Noah', 'Owen', 'Parker', 'Paul', 'Peter', 'Quinn', 'Randy', 'Ray', 'Reed', 'Rhys',
  'Ryan', 'Sam', 'Scott', 'Sean', 'Seth', 'Shawn', 'Silas', 'Simon', 'Theo', 'Tyler',
  'Victor', 'Wade', 'Wayne', 'Zack',
];
const FAKE_PLAYER_POOL = FAKE_PLAYER_NAMES.map((name, index) => ({
  id: `fake:${index + 1}`,
  name,
  username: `${name.toLowerCase()}${index + 1}`,
  platform: 'Arena Player',
  isFake: true,
}));

function hashString(value) {
  const input = String(value || '');
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createSeededRandom(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6D2B79F5;
    let next = Math.imul(t ^ (t >>> 15), 1 | t);
    next ^= next + Math.imul(next ^ (next >>> 7), 61 | next);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleFakePlayerPool(viewerSeed) {
  const list = [...FAKE_PLAYER_POOL];
  const random = createSeededRandom(viewerSeed);
  for (let i = list.length - 1; i > 0; i -= 1) {
    const swapIndex = Math.floor(random() * (i + 1));
    [list[i], list[swapIndex]] = [list[swapIndex], list[i]];
  }
  return list;
}

function buildFakeLobbyUsers(userId, realUsersCount, now = Date.now()) {
  const refreshBucket = Math.floor(now / FAKE_PLAYER_REFRESH_MS);
  const viewerSeed = hashString(`${userId || 'guest'}:${refreshBucket}`);
  const desiredCount = Math.min(
    FAKE_PLAYER_POOL.length,
    Math.max(12, FAKE_PLAYER_BASE_COUNT - Math.min(realUsersCount || 0, 8))
  );
  const orderedPool = shuffleFakePlayerPool(viewerSeed);
  return orderedPool
    .slice(0, desiredCount)
    .map((player) => ({
      ...player,
      onlineStats: { wins: 0, losses: 0, total: 0 },
    }));
}

function roundToTwo(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function formatCoins(value) {
  const n = roundToTwo(value);
  return n % 1 === 0 ? String(Math.trunc(n)) : String(n);
}

export default function OnlineLobby({ token, user, onBackToGame, onOpenHistory, onMatchStart, onWalletChange, initialMatchMode }) {
  const socketRef = useRef(null);
  const outgoingReqRef = useRef(null);
  const opponentRailRef = useRef(null);
  const toastTimerRef = useRef(null);
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 720,
  });
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [fakeUsers, setFakeUsers] = useState([]);
  const [incomingReq, setIncomingReq] = useState(null);
  const [incomingTournamentReq, setIncomingTournamentReq] = useState(null);
  const [notice, setNotice] = useState('');
  const [toast, setToast] = useState(null);
  const [outgoingReqTo, setOutgoingReqTo] = useState(null);
  const [myFriendlyMatchesToday, setMyFriendlyMatchesToday] = useState(null);
  const [stake, setStake] = useState(10);
  const [targetWins, setTargetWins] = useState(5);
  const [menu, setMenu] = useState(MENU.opponent);
  const [matchMode, setMatchMode] = useState(initialMatchMode || 'coin');
  const [searchTerm, setSearchTerm] = useState('');
  const isExternal = Boolean(user?.isExternal);
  const userCoins = Number(user?.coins || 0);
  const platformFee = matchMode === 'friendly' ? 0 : roundToTwo(stake * 0.1);
  const totalEntryCost = matchMode === 'friendly' ? 0 : roundToTwo(stake + platformFee);

  function showErrorToast(message) {
    setToast({
      type: 'error',
      title: 'Insufficient balance',
      message: String(message || ''),
    });
  }

  function maybeShowInsufficientBalanceToast(message) {
    const text = String(message || '').trim();
    if (!text) return false;

    const selectedPlayerMatch = text.match(/^Selected player needs ([\d.]+) coins including fee\./i);
    if (selectedPlayerMatch) {
      const coins = Number(selectedPlayerMatch[1] || 0);
      showErrorToast(`Selected player does not have sufficient coins to play. You need ${formatCoins(coins)} coins including fee to start this match.`);
      return true;
    }

    const youNeedMatch = text.match(/^You need ([\d.]+) coins including fee\b/i);
    if (youNeedMatch) {
      showErrorToast(text);
      return true;
    }

    if (/insufficient/i.test(text) && /coin/i.test(text)) {
      showErrorToast(text);
      return true;
    }

    return false;
  }

  useEffect(() => {
    if (!toast) return undefined;
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 4500);
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [toast]);

  useEffect(() => {
    function handleResize() {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => {
      setConnected(false);
      setMyFriendlyMatchesToday(null);
    });
    socket.on('online:users', (users) => {
      const list = users || [];
      setOnlineUsers(list);
      if (outgoingReqRef.current && !String(outgoingReqRef.current).startsWith('fake:') && !list.some((u) => u.id === outgoingReqRef.current)) {
        setOutgoingReqTo(null);
        outgoingReqRef.current = null;
      }
    });
    socket.on('online:me-stats', ({ friendlyMatchesToday }) => {
      const n = Number(friendlyMatchesToday);
      setMyFriendlyMatchesToday(Number.isFinite(n) ? n : 0);
    });
    socket.on('online:request-received', ({ from, stake: reqStake, targetWins: reqTargetWins, matchMode: reqMatchMode }) => setIncomingReq({ ...from, stake: reqStake, targetWins: reqTargetWins || 1, matchMode: reqMatchMode }));
    socket.on('online:request-sent', ({ toUserId }) => {
      outgoingReqRef.current = toUserId || null;
      setOutgoingReqTo(toUserId || null);
      setNotice('');
    });
    socket.on('online:request-error', ({ message }) => {
      const fallback = message || 'Request failed';
      outgoingReqRef.current = null;
      setOutgoingReqTo(null);
      if (maybeShowInsufficientBalanceToast(fallback)) return;
      setNotice(fallback);
    });
    socket.on('online:request-response', ({ fromUser, accepted }) => {
      outgoingReqRef.current = null;
      setOutgoingReqTo(null);
      setNotice(accepted ? `${fromUser.name} accepted your request` : `${fromUser.name} rejected your request`);
    });
    socket.on('online:request-cancelled', ({ fromUserId, reason }) => {
      // Clear any outgoing request state
      outgoingReqRef.current = null;
      setOutgoingReqTo(null);
      // Clear incoming request modal if it was from this user, or if fromUserId is null (all requests cancelled)
      if (incomingReq && (fromUserId === null || incomingReq.id === fromUserId)) {
        setIncomingReq(null);
      }
      setNotice(reason || 'Request cancelled by another player');
    });
    socket.on('tournament:request-received', ({ requestId, tournamentId, tournamentName, roundNumber, from }) => {
      setIncomingTournamentReq({ requestId, tournamentId, tournamentName, roundNumber, from });
      setNotice(`Tournament request received for ${tournamentName}.`);
    });
    socket.on('tournament:request-response', ({ fromUser, accepted }) => {
      setNotice(accepted ? `${fromUser.name} accepted the tournament request` : `${fromUser.name} rejected the tournament request`);
    });
    socket.on('tournament:request-closed', () => {
      setIncomingTournamentReq(null);
      setNotice('This tournament request was accepted by another player.');
    });
    socket.on('online:match-start', ({ roomId, opponent, color, state, stake: matchStake, session, tournament }) => {
      setNotice(`Match found with ${opponent.name}`);
      // Clear any incoming request modal since match is starting
      setIncomingReq(null);
      if (typeof onWalletChange === 'function') onWalletChange();
      if (typeof onMatchStart === 'function') {
        onMatchStart({ roomId, opponent, color, state, stake: matchStake, session, tournament });
      }
    });
    socket.on('online:match-resume', ({ roomId, opponent, color, state, stake: matchStake, reconnect, session, chatHistory }) => {
      setNotice(`Returning you to your unfinished match with ${opponent.name}`);
      if (typeof onMatchStart === 'function') {
        onMatchStart({ roomId, opponent, color, state, stake: matchStake, reconnect, session, chatHistory, resumed: true });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [token, onMatchStart, onWalletChange]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    function refreshFakeUsers() {
      setFakeUsers(buildFakeLobbyUsers(user?.id, onlineUsers.length));
    }

    refreshFakeUsers();
    const timer = setInterval(refreshFakeUsers, FAKE_PLAYER_REFRESH_MS);
    return () => clearInterval(timer);
  }, [user?.id, onlineUsers.length]);

  useEffect(() => {
    if (!String(outgoingReqRef.current || '').startsWith('fake:')) return;
    if (fakeUsers.some((player) => player.id === outgoingReqRef.current)) return;
    outgoingReqRef.current = null;
    setOutgoingReqTo(null);
  }, [fakeUsers]);

  useEffect(() => {
    if (initialMatchMode && matchMode !== initialMatchMode) {
      setMatchMode(initialMatchMode);
    }
  }, [initialMatchMode]);

  function sendRequest(toUserId) {
    if (matchMode === 'coin' && userCoins < totalEntryCost) {
      showErrorToast(`You need ${formatCoins(totalEntryCost)} coins including fee to start this match.`);
      return;
    }

    // Prevent sending multiple requests to the same user
    if (outgoingReqTo === toUserId) {
      setNotice('Request already sent to this player');
      return;
    }

    const fakeTarget = fakeUsers.find((player) => player.id === toUserId);
    if (fakeTarget) {
      outgoingReqRef.current = toUserId;
      setOutgoingReqTo(toUserId);
      setNotice('');
      window.setTimeout(() => {
        if (outgoingReqRef.current === toUserId) {
          outgoingReqRef.current = null;
          setOutgoingReqTo((current) => (current === toUserId ? null : current));
        }
      }, 8000);
      return;
    }
    setNotice('');
    socketRef.current?.emit('online:send-request', { toUserId, stake, targetWins, matchMode });
  }

  function respondRequest(accepted) {
    if (!incomingReq) return;
    const incomingStake = Number(incomingReq.stake || 0);
    const incomingTotal = roundToTwo(incomingStake + roundToTwo(incomingStake * 0.1));
    if (accepted && userCoins < incomingTotal) {
      showErrorToast(`You need ${formatCoins(incomingTotal)} coins including fee to accept this match.`);
      return;
    }
    socketRef.current?.emit('online:respond-request', {
      fromUserId: incomingReq.id,
      accepted,
    });
    setIncomingReq(null);
  }

  function respondTournamentRequest(accepted) {
    if (!incomingTournamentReq) return;
    socketRef.current?.emit('tournament:respond-request', {
      requestId: incomingTournamentReq.requestId,
      accepted,
    });
    setIncomingTournamentReq(null);
  }

  const statusText = useMemo(() => (connected ? 'Connected' : 'Connecting...'), [connected]);
  const lobbyUsers = useMemo(() => [...onlineUsers, ...fakeUsers], [onlineUsers, fakeUsers]);
  const filteredUsers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return lobbyUsers;
    return lobbyUsers.filter((player) => {
      const haystack = [
        player.name,
        player.username,
        player.email,
        player.platform,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [lobbyUsers, searchTerm]);
  const sortedUsers = useMemo(() => {
    const rankFor = (player) => {
      if (player?.isFake) return 3;
      const isPending = outgoingReqTo === player?.id;
      const isBusyForMode = Boolean(
        matchMode === 'coin'
          ? player?.activeMatchType === 'coin'
          : Boolean(player?.activeMatchType)
      );

      if (!isPending && !isBusyForMode) return 0;
      if (isPending) return 1;
      return 2;
    };

    return [...filteredUsers].sort((a, b) => {
      const ra = rankFor(a);
      const rb = rankFor(b);
      if (ra !== rb) return ra - rb;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [filteredUsers, matchMode, outgoingReqTo]);
  const isCompact = viewport.width < 980;
  const isShort = viewport.height < 760;
  const isTight = viewport.height < 920 || viewport.width < 1450;
  const showAllStakeCards = isCompact;

  function scrollOpponentRail(direction) {
    const rail = opponentRailRef.current;
    if (!rail) return;
    const distance = Math.max(rail.clientWidth * 0.75, 220);
    rail.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  }

  return (
    
    <div style={styles.page}>
      <style>{`
  .opponent-rail::-webkit-scrollbar {
    display: none;
  }
`}</style>
      {toast && toast.type === 'error' && (
        <div style={{ ...styles.toastWrap, ...(isCompact ? styles.toastWrapCompact : {}) }}>
          <div style={styles.toast}>
            <div style={styles.toastHeader}>
              <div style={styles.toastTitle}>{toast.title}</div>
              <button type="button" style={styles.toastClose} onClick={() => setToast(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div style={styles.toastMessage}>{toast.message}</div>
          </div>
        </div>
      )}
      <div
        style={{
          ...styles.layout,
          gridTemplateColumns: isCompact ? '1fr' : '260px minmax(0,1fr)',
          gridTemplateRows: isCompact ? 'auto minmax(0,1fr)' : '1fr',
          minHeight: isCompact ? 'auto' : styles.layout.minHeight,
        }}
      >
        <aside style={styles.sidebar}>
          {isCompact ? (
            <>
              <div style={{ ...styles.brand, ...styles.brandCompact }}>Online Arena</div>
              <div style={styles.sidebarCompactGrid}>
                <button
                  type="button"
                  onClick={() => {
                    setMatchMode('coin');
                    setMenu(MENU.opponent);
                  }}
                  style={{ ...menuBtn(matchMode === 'coin', true), ...styles.sidebarCompactBtn, width: '100%' }}
                >
                  Real Match
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMatchMode('friendly');
                    setMenu(MENU.opponent);
                  }}
                  style={{ ...menuBtn(matchMode === 'friendly', true), ...styles.sidebarCompactBtn, width: '100%' }}
                >
                  Friendly Match
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (isExternal && typeof onOpenHistory === 'function') {
                      onOpenHistory();
                      return;
                    }
                    setMenu(MENU.info);
                  }}
                  style={{ ...menuBtn(menu === MENU.info, true), ...styles.sidebarCompactBtn, width: '100%' }}
                >
                  {isExternal ? 'Records' : 'Arena Info'}
                </button>
                <button
                  type="button"
                  onClick={onBackToGame}
                  style={{ ...styles.sidebarCompactBtn, ...styles.sidebarCompactBackBtn, width: '100%', fontWeight: '800', color: '#ffffff', background: '#5a3f28', borderRadius: '6px' ,border: '2px solid #8a5c36' }}
                >
                  {isExternal ? 'Return' : 'Back'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={styles.brand}>Online Arena</div>
              <button
                type="button"
                onClick={() => {
                  setMatchMode('coin');
                  setMenu(MENU.opponent);
                }}
                style={{ ...styles.sidebarBtn, ...menuBtn(matchMode === 'coin', false) }}
              >
                Real Match
              </button>
              <button
                type="button"
                onClick={() => {
                  setMatchMode('friendly');
                  setMenu(MENU.opponent);
                }}
                style={{ ...styles.sidebarBtn, ...menuBtn(matchMode === 'friendly', false) }}
              >
                Friendly Match
              </button>
              <button
                type="button"
                onClick={() => (isExternal && typeof onOpenHistory === 'function' ? onOpenHistory() : setMenu(MENU.info))}
                style={{ ...styles.sidebarBtn, ...menuBtn(menu === MENU.info, false) }}
              >
                {isExternal ? 'Records' : 'Arena Info'}
              </button>
              <button
                type="button"
                onClick={onBackToGame}
                style={{
                  marginTop: 'auto',
                  background: '#5a3f28',
                  border: '2px solid #8a5c36',
                  color: '#d8c29a',
                  padding: 'clamp(8px, 1vw, 12px) clamp(10px, 1.5vw, 14px)',
                  borderRadius: 'clamp(8px, 1vw, 12px)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 'clamp(0.85rem, 1.3vw, 1rem)',
                  transition: 'all 200ms ease',
                  width: '100%',
                  flexShrink: 0,
                }}
              >
                {isExternal ? 'Return' : 'Back'}
              </button>
            </>
          )}
        </aside>

        <main
          style={{
            ...styles.mainCard,
            minHeight: isCompact ? 'auto' : styles.mainCard.minHeight,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={styles.headerRow}>
            <div>
              <h2 style={styles.title}>
                {menu === MENU.opponent
                  ? 'Choose Opponent'
                  : 'Arena Info'}
              </h2>
              <div style={{ ...styles.subTitle, ...(isTight ? styles.subTitleCompact : {}) }}>
                {user?.name}{user?.email ? ` (${user.email})` : ''} - {statusText} - Coins: {formatCoins(user?.coins ?? 0)}
              </div>
              {/* {matchMode === 'friendly' && Number.isFinite(myFriendlyMatchesToday) && (
                <div style={{ ...styles.subTitle, ...(isTight ? styles.subTitleCompact : {}), marginTop: 6 }}>
                  Aaj aapne friendly khela: {Math.max(0, Number(myFriendlyMatchesToday || 0))}/2
                </div>
              )} */}
            </div>
          </div>

          {outgoingReqTo && <div style={styles.noticeOk}>Request sent. Waiting for response...</div>}
          {notice && <div style={styles.notice}>{notice}</div>}

          {menu === MENU.opponent && (
            <div style={{ ...styles.contentArea, flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ ...styles.stakePanel, ...(isTight ? styles.stakePanelCompact : {}), marginBottom: 'clamp(6px, 0.8vw, 8px)' }}>
                <div style={styles.stakeHeadingRow}>
                  <div style={{ ...styles.stakeSelected, ...(isTight ? styles.stakeSelectedCompact : {}) }}>{matchMode === 'friendly' ? '0 coins' : `${stake} + ${formatCoins(platformFee)}`}</div>
                </div>
                <div style={styles.targetWinRow}>
                  <div style={styles.targetWinLabel}>Play To</div>
                  <div style={styles.targetWinOptions}>
                    {targetWinOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setTargetWins(option)}
                        style={{
                          ...styles.targetWinBtn,
                          ...(targetWins === option ? styles.targetWinBtnActive : {}),
                        }}
                      >
                        {option} Win{option > 1 ? 's' : ''}
                      </button>
                    ))}
                  </div>
                </div>
                {matchMode === 'coin' && (
                  <div
                    style={{
                      ...styles.stakeCards,
                      ...(showAllStakeCards
                        ? styles.stakeCardsMobile
                        : (isTight
                          ? styles.stakeCardsCompact
                          : {
                              gridAutoColumns: isCompact ? 'minmax(132px, 1fr)' : 'minmax(150px, 1fr)',
                            })),
                    }}
                  >
                    {stakeOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setStake(option.value)}
                        style={{
                          ...styles.stakeCard,
                          ...(showAllStakeCards
                            ? styles.stakeCardMobile
                            : (isTight ? styles.stakeCardCompact : { padding: isShort ? 10 : 12 })),
                          ...(stake === option.value ? styles.stakeCardActive : {}),
                        }}
                      >
                        <div style={{ ...styles.stakeCardTitle, ...(showAllStakeCards ? styles.stakeCardTitleMobile : (isTight ? styles.stakeCardTitleCompact : {})) }}>{option.label}</div>
                        {showAllStakeCards ? (
                          <div style={styles.stakeCardValueRowMobile}>
                            <span style={{ ...styles.stakeCardValue, ...styles.stakeCardValueMobile }}>{option.value}</span>
                            <span style={{ ...styles.stakeCardMeta, ...styles.stakeCardMetaMobile, ...styles.stakeCardCoinsInlineMobile }}>coins</span>
                          </div>
                        ) : (
                          <>
                            <div style={{ ...styles.stakeCardValue, ...(isTight ? styles.stakeCardValueCompact : {}) }}>{option.value}</div>
                            <div style={{ ...styles.stakeCardMeta, ...(isTight ? styles.stakeCardMetaCompact : {}) }}>coins</div>
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ ...styles.playersSection, flex: '1 1 auto', minHeight: 0 }}>
                <div style={styles.playersHeader}>
                  <div style={styles.sectionTitle}>{matchMode === 'friendly' ? '2 Friendly Matches can be played daily' : 'Available Players'}</div>
                  <div style={styles.railControls}>
                    <button type="button" onClick={() => scrollOpponentRail('left')} style={styles.railButton} aria-label="Scroll players left">
                      ‹
                    </button>
                    <button type="button" onClick={() => scrollOpponentRail('right')} style={styles.railButton} aria-label="Scroll players right">
                      ›
                    </button>
                  </div>
                </div>
                {matchMode === 'friendly' && (
                  <div style={styles.friendlyStatsCard}>
                    <div style={styles.friendlyStatsPill}>
                      <div style={styles.friendlyStatsLabel}>Played Today</div>
                      <div style={styles.friendlyStatsValue}>
                        {Number.isFinite(myFriendlyMatchesToday) ? Math.max(0, Number(myFriendlyMatchesToday || 0)) : '-'}/2
                      </div>
                    </div>
                    <div style={styles.friendlyStatsPill}>
                      <div style={styles.friendlyStatsLabel}>Limit</div>
                      <div style={styles.friendlyStatsValue}>2</div>
                    </div>
                  </div>
                )}
                <div style={styles.searchWrap}>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by username or player name"
                    style={styles.searchInput}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      style={styles.searchClearBtn}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div
                  ref={opponentRailRef}
                  className="opponent-rail"
                  style={{
                    ...styles.opponentRail,
                    gridAutoColumns: isCompact ? 'minmax(180px, 210px)' : 'minmax(190px, 220px)',
                  }}
                >
                  {lobbyUsers.length === 0 && (
                    <div style={{ padding: 12, color: '#ffffff' }}>No other users online right now.</div>
                  )}
                  {lobbyUsers.length > 0 && filteredUsers.length === 0 && (
                    <div style={{ padding: 12, color: '#ffffff' }}>No players match your search.</div>
                  )}
                  {sortedUsers.map((u) => (
                    (() => {
                      const isBusyForMode = Boolean(
                        u.isFake
                        || (matchMode === 'coin'
                          ? u.activeMatchType === 'coin'
                          : Boolean(u.activeMatchType))
                      );
                      const isPending = outgoingReqTo === u.id;
                      const isDisabled = isPending || isBusyForMode;

                      return (
                        <div
                          key={u.id}
                          style={{
                            ...styles.opponentCard,
                            ...(isTight ? styles.opponentCardTight : {}),
                            ...(isBusyForMode ? styles.opponentCardBusy : {}),
                          }}
                        >
                          <div style={styles.opponentGlow} />
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: isShort ? 10 : 12,
                              position: 'relative',
                              zIndex: 1,
                            }}
                          >
                            <div style={styles.avatar}>{(u.name?.[0] || 'U').toUpperCase()}</div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  color: '#ffffff',
                                  fontSize: isTight ? 14 : (isShort ? 15 : 16),
                                  fontWeight: 700,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {u.name}
                              </div>
                              <div
                                style={{
                                  color: 'rgba(255,255,255,0.85)',
                                  fontSize: isTight ? 11 : (isShort ? 12 : 13),
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {u.username ? `@${u.username}` : (u.platform || 'Arena Player')}
                              </div>
                              {/* {Number.isFinite(u.friendlyMatchesToday) && (
                                <div
                                  style={{
                                    color: 'rgba(255,255,255,0.8)',
                                    fontSize: isTight ? 10 : (isShort ? 11 : 12),
                                    marginTop: 4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Friendly played today: {Math.max(0, Number(u.friendlyMatchesToday || 0))}/2
                                </div>
                              )} */}
                        </div>
                      </div>
                          <button
                            type="button"
                            onClick={() => sendRequest(u.id)}
                            disabled={isDisabled}
                            style={{
                              ...actionBtn(isBusyForMode ? '#4f3a22' : (isPending ? '#5f704f' : '#3f5d2f')),
                              ...(isTight ? styles.actionBtnCompact : {}),
                              ...(isBusyForMode ? styles.actionBtnDisabled : {}),
                              width: '100%',
                              position: 'relative',
                              zIndex: 1,
                              marginTop: 'auto',
                            }}
                          >
                            {isBusyForMode ? 'Game in Progress' : (isPending ? 'Request Sent' : 'Send Request')}
                          </button>
                        </div>
                      );
                    })()
                  ))}
                </div>
              </div>
            </div>
          )}

          {menu === MENU.info && (
            <div style={styles.contentArea}>
              <div style={{ ...styles.infoCard, overflowY: 'auto' }}>
                <div style={styles.infoTitle}>Arena Info</div>

                <div style={styles.infoLine}>- Winner: first to <strong>{targetWins}</strong> wins.</div>
                <div style={styles.infoLine}>- Cost: stake + fee (shown next to stake).</div>
                <div style={styles.infoLine}>- Coins are locked while match is running; settled after it ends.</div>
                <div style={styles.infoLine}>- Need enough coins to start/accept, otherwise “Insufficient balance”.</div>
                <div style={styles.infoLine}>- Quit = loss. If you stay disconnected for 5 minutes, you will lose automatically.</div>
              </div>
            </div>
          )}
        </main>
      </div>
      {incomingReq && (
        <div style={styles.modalOverlay}>
          <div style={styles.requestModal}>
            <div style={styles.requestModalTitle}>Match Request</div>
            <div style={styles.requestModalText}>
              <strong>{incomingReq.name}</strong> sent a <strong>first to {incomingReq.targetWins || 1}</strong> {incomingReq.matchMode === 'friendly' ? 'friendly' : 'coin'} session request
              {incomingReq.matchMode === 'friendly'
                ? '.'
                : (
                  <>
                    {' '}with <strong>{incomingReq.stake} coins</strong> stake and <strong>{formatCoins(roundToTwo(Number(incomingReq.stake || 0) * 0.1))} coins</strong> fee.
                  </>
                )}
            </div>
            <div style={styles.requestModalActions}>
              <button type="button" onClick={() => respondRequest(true)} style={{ ...actionBtn('#3f5d2f'), flex: 1 }}>
                Accept
              </button>
              <button type="button" onClick={() => respondRequest(false)} style={{ ...actionBtn('#643030'), flex: 1 }}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
      {incomingTournamentReq && (
        <div style={styles.modalOverlay}>
          <div style={styles.requestModal}>
            <div style={styles.requestModalTitle}>Tournament Request</div>
            <div style={styles.requestModalText}>
              <b>{incomingTournamentReq.from?.name}</b> wants to play a tournament match in <b>{incomingTournamentReq.tournamentName}</b>, round <b>{incomingTournamentReq.roundNumber}</b>.
            </div>
            <div style={styles.requestModalActions}>
              <button type="button" onClick={() => respondTournamentRequest(true)} style={{ ...actionBtn('#3f5d2f'), flex: 1 }}>
                Accept
              </button>
              <button type="button" onClick={() => respondTournamentRequest(false)} style={{ ...actionBtn('#643030'), flex: 1 }}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100dvh',
    background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 65%)',
    padding: 'clamp(8px, 1.1vw, 12px)',
    overflowX: 'hidden',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  layout: {
    maxWidth: '100%',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'clamp(200px, 20vw, 280px) minmax(0,1fr)',
    gap: 'clamp(8px, 1.1vw, 12px)',
    flex: 1,
    minHeight: 'calc(100dvh - clamp(24px, 3vw, 32px))',
    padding: '0 clamp(0px, 1vw, 12px)',
    width: '100%',
  },
  sidebar: {
    background: 'rgba(22, 12, 8, 0.92)',
    border: '2px solid rgba(212,165,88,0.35)',
    borderRadius: 'clamp(10px, 1.5vw, 16px)',
    padding: 'clamp(8px, 1.1vw, 12px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(6px, 1vw, 10px)',
    minHeight: 0,
    height: '100%',
  },
  brand: {
    color: '#f6e7c1',
    fontSize: 'clamp(1.15rem, 2.4vw, 1.55rem)',
    fontWeight: 800,
    marginBottom: 'clamp(6px, 1vw, 10px)',
  },
  brandCompact: {
    width: '100%',
    textAlign: 'center',
    marginBottom: 0,
    fontSize: '1.05rem',
  },
  sidebarCompactRow: {
    width: '100%',
    display: 'flex',
    gap: 8,
  },
  sidebarCompactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '8px',
    width: '100%',
  },
  sidebarCompactBtn: {
    flex: '1 1 0',
    minWidth: 0,
    padding: '8px 10px',
    fontSize: '0.9rem',
  },
  sidebarCompactBackBtn: {
    background: '#7a4c22',
  },
  sidebarBtn: {
    width: '100%',
    padding: 'clamp(10px, 1vw, 14px) clamp(12px, 1.2vw, 16px)',
    borderRadius: 'clamp(8px, 1vw, 12px)',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 'clamp(0.95rem, 1.2vw, 1rem)',
    textAlign: 'left',
    transition: 'all 200ms ease',
    border: '2px solid #d7b16a',
    background: '#3a2314',
    color: '#ffffff',
  },
  mainCard: {
    background: 'rgba(22, 12, 8, 0.92)',
    border: '2px solid rgba(212,165,88,0.35)',
    borderRadius: 'clamp(10px, 1.5vw, 16px)',
    padding: 'clamp(10px, 1.1vw, 14px)',
    minHeight: 0,
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 'clamp(6px, 1vw, 12px)',
    gap: 'clamp(8px, 1.5vw, 14px)',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: 'clamp(1.28rem, 3vw, 1.7rem)',
    fontWeight: 800,
  },
  subTitle: {
    color: '#ffffff',
    fontSize: 'clamp(0.9rem, 1.5vw, 1rem)',
    marginTop: 4,
  },
  subTitleCompact: {
    fontSize: '0.82rem',
  },
  incomingCard: {
    background: 'rgba(83, 57, 21, 0.68)',
    border: '2px solid rgba(212,165,88,0.35)',
    borderRadius: 'clamp(8px, 1.5vw, 12px)',
    padding: 'clamp(10px, 1.5vw, 14px)',
    marginBottom: 'clamp(8px, 1.5vw, 12px)',
    color: '#f6e7c1',
    fontSize: 'clamp(0.9rem, 1.5vw, 1.05rem)',
  },
  notice: {
    color: '#ffd48a',
    marginBottom: 'clamp(6px, 1vw, 10px)',
    fontSize: 'clamp(0.9rem, 1.5vw, 1rem)',
    fontWeight: 600,
    flexShrink: 0,
  },
  noticeOk: {
    color: '#b8f0b0',
    marginBottom: 'clamp(6px, 1vw, 10px)',
    fontSize: 'clamp(0.9rem, 1.5vw, 1rem)',
    fontWeight: 600,
    flexShrink: 0,
  },
  toastWrap: {
    position: 'fixed',
    top: 90,
    right: 18,
    zIndex: 9999,
    width: 'min(92vw, 360px)',
    pointerEvents: 'none',
  },
  toastWrapCompact: {
    top: 72,
    right: 12,
    width: 'min(94vw, 360px)',
  },
  toast: {
    pointerEvents: 'auto',
    borderRadius: 14,
    border: '1.5px solid rgba(255, 82, 82, 0.65)',
    background: 'linear-gradient(180deg, rgba(120, 18, 18, 0.98), rgba(40, 8, 8, 0.98))',
    boxShadow: '0 18px 44px rgba(0,0,0,0.42)',
    padding: '12px 12px 12px 12px',
    color: '#ffe4e4',
  },
  toastHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 6,
  },
  toastTitle: {
    fontWeight: 900,
    color: '#ffb0b0',
    letterSpacing: 0.2,
    fontSize: '0.98rem',
  },
  toastMessage: {
    color: '#ffecec',
    lineHeight: 1.35,
    fontSize: '0.95rem',
  },
  toastClose: {
    border: 'none',
    background: 'transparent',
    color: '#ffe4e4',
    cursor: 'pointer',
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
    padding: 0,
  },
  stakePanel: {
    display: 'grid',
    gap: 'clamp(5px, 0.8vw, 8px)',
    marginBottom: 'clamp(6px, 0.8vw, 8px)',
    padding: 'clamp(7px, 0.8vw, 10px)',
    border: '2px solid rgba(212,165,88,0.28)',
    borderRadius: 'clamp(12px, 2vw, 18px)',
    background: 'linear-gradient(145deg, rgba(74,43,22,0.75), rgba(33,18,10,0.92))',
    boxShadow: '0 18px 34px rgba(0,0,0,0.2)',
    flexShrink: 0,
  },
  stakePanelCompact: {
    gap: 6,
    padding: 8,
    marginBottom: 8,
  },
  stakeHeadingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'clamp(8px, 1.5vw, 14px)',
    flexWrap: 'wrap',
  },
  stakeEyebrow: {
    color: '#f0c36a',
    fontSize: 'clamp(0.7rem, 1vw, 0.82rem)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
    marginBottom: 2,
  },
  stakeTitle: {
    color: '#ffffff',
    fontSize: 'clamp(0.92rem, 1.5vw, 1.05rem)',
    fontWeight: 800,
    lineHeight: 1.15,
  },
  stakeTitleCompact: {
    fontSize: '1rem',
    lineHeight: 1.15,
  },
  stakeSelected: {
    alignSelf: 'center',
    border: '2px solid rgba(215,177,106,0.4)',
    borderRadius: 999,
    padding: 'clamp(5px, 0.8vw, 8px) clamp(9px, 1.2vw, 12px)',
    color: '#fff0cc',
    background: 'rgba(24,12,7,0.8)',
    fontWeight: 800,
    fontSize: 'clamp(0.8rem, 1.1vw, 0.92rem)',
    whiteSpace: 'nowrap',
  },
  stakeSelectedCompact: {
    padding: '5px 10px',
    fontSize: '0.82rem',
  },
  targetWinRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  targetWinLabel: {
    color: '#f0c36a',
    fontSize: '0.76rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  targetWinOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  targetWinBtn: {
    border: '1px solid rgba(215,177,106,0.35)',
    background: '#2f1a10',
    color: '#f6e7c1',
    borderRadius: 999,
    padding: '6px 10px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  targetWinBtnActive: {
    background: '#7a4c22',
    borderColor: '#f0c36a',
    boxShadow: '0 0 0 1px rgba(240,195,106,0.25)',
  },
  stakeCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(104px, 15vw, 132px), 1fr))',
    gap: 'clamp(5px, 0.8vw, 8px)',
    paddingBottom: 2,
    scrollbarWidth: 'thin',
  },
  stakeCardsCompact: {
    gridTemplateColumns: 'repeat(5, minmax(120px, 1fr))',
    overflowX: 'auto',
    overflowY: 'hidden',
    gap: 8,
    paddingBottom: 4,
  },
  stakeCardsMobile: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    overflowX: 'hidden',
    overflowY: 'visible',
    gap: 10,
    paddingBottom: 0,
  },
  stakeCard: {
    border: '2px solid rgba(215,177,106,0.3)',
    borderRadius: 'clamp(10px, 1.5vw, 14px)',
    background: 'linear-gradient(180deg, rgba(55,31,18,0.96), rgba(27,14,8,0.98))',
    color: '#f6e7c1',
    padding: 'clamp(6px, 0.7vw, 8px)',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 180ms ease',
    boxShadow: '0 8px 18px rgba(0,0,0,0.18)',
  },
  stakeCardCompact: {
    padding: 8,
    minWidth: 112,
  },
  stakeCardMobile: {
    padding: 10,
    minWidth: 0,
  },
  stakeCardActive: {
    transform: 'translateY(-2px) scale(1.01)',
    borderColor: '#f0c36a',
    boxShadow: '0 14px 28px rgba(0,0,0,0.28)',
    background: 'linear-gradient(180deg, rgba(122,76,34,0.96), rgba(64,34,17,0.98))',
  },
  stakeCardTitle: {
    color: '#f0c36a',
    fontSize: 'clamp(0.68rem, 0.95vw, 0.8rem)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 'clamp(4px, 0.7vw, 6px)',
  },
  stakeCardTitleCompact: {
    fontSize: '0.72rem',
    marginBottom: 4,
  },
  stakeCardTitleMobile: {
    fontSize: '0.74rem',
    marginBottom: 4,
  },
  stakeCardValue: {
    fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
    fontWeight: 800,
    lineHeight: 1,
  },
  stakeCardValueCompact: {
    fontSize: '1.15rem',
  },
  stakeCardValueMobile: {
    fontSize: '1.25rem',
  },
  stakeCardValueRowMobile: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    lineHeight: 1,
  },
  stakeCardMeta: {
    color: '#d8c29a',
    fontSize: 'clamp(0.68rem, 0.95vw, 0.78rem)',
    marginTop: 2,
    marginBottom: 'clamp(2px, 0.6vw, 4px)',
  },
  stakeCardMetaCompact: {
    fontSize: '0.72rem',
    marginBottom: 0,
  },
  stakeCardMetaMobile: {
    fontSize: '0.75rem',
    marginBottom: 0,
  },
  stakeCardCoinsInlineMobile: {
    marginTop: 0,
  },
  stakeCardBlurb: {
    color: '#f6e7c1',
    fontSize: 'clamp(0.62rem, 0.9vw, 0.74rem)',
    lineHeight: 1.35,
  },
  sectionTitle: {
    color: '#ffffff',
    marginBottom: 'clamp(6px, 1vw, 10px)',
    fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
    fontWeight: 700,
    flexShrink: 0,
  },
  friendlyStatsCard: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid rgba(215,177,106,0.35)',
    background: 'rgba(33, 18, 11, 0.55)',
    marginBottom: 'clamp(6px, 1vw, 8px)',
  },
  friendlyStatsPill: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 999,
    border: '1px solid rgba(215,177,106,0.32)',
    background: 'rgba(22, 12, 8, 0.55)',
  },
  friendlyStatsLabel: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  friendlyStatsValue: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 800,
  },
  searchWrap: {
    display: 'flex',
    gap: 'clamp(6px, 1vw, 10px)',
    alignItems: 'center',
    marginBottom: 'clamp(6px, 1vw, 8px)',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    minWidth: 'clamp(160px, 30vw, 100%)',
    background: '#2f1a10',
    border: '2px solid rgba(215,177,106,0.45)',
    color: '#ffffff',
    borderRadius: 'clamp(8px, 1vw, 12px)',
    padding: 'clamp(8px, 1vw, 12px) clamp(10px, 1.5vw, 14px)',
    outline: 'none',
    fontSize: 'clamp(0.9rem, 1.5vw, 1rem)',
  },
  searchClearBtn: {
    border: '2px solid #d7b16a',
    background: '#3a2314',
    color: '#f6e7c1',
    borderRadius: 'clamp(8px, 1vw, 12px)',
    padding: 'clamp(6px, 1vw, 10px) clamp(10px, 1.5vw, 14px)',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 'clamp(0.85rem, 1.3vw, 1rem)',
    transition: 'all 150ms ease',
  },
  contentArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    minHeight: 'fit-content',
    gap: 'clamp(6px, 1vw, 10px)',
    overflowY: 'visible',
    overflowX: 'hidden',
  },
  modeButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginTop: 'auto',
    marginBottom: 'clamp(10px, 1vw, 14px)',
    paddingBottom: 'clamp(6px, 0.8vw, 10px)',
    borderBottom: '2px solid rgba(212,165,88,0.25)',
  },
  modeButtonsCompact: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
    justifyContent: 'center',
  },
  modeBtn: {
    border: '2px solid #d7b16a',
    background: '#3a2314',
    color: '#d8c29a',
    padding: 'clamp(6px, 0.8vw, 10px) clamp(8px, 1vw, 12px)',
    borderRadius: 'clamp(6px, 1vw, 10px)',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 'clamp(0.8rem, 1.2vw, 0.95rem)',
    transition: 'all 200ms ease',
    width: '100%',
  },
  modeBtnActive: {
    background: 'linear-gradient(135deg, #8a5a2b, #6a4820)',
    color: '#ffd48a',
    borderColor: '#ffd48a',
    boxShadow: '0 0 0 1px rgba(255,212,138,0.25)',
  },
  playersSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    minHeight: 'fit-content',
  },
  playersHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 'clamp(6px, 1vw, 10px)',
    flexWrap: 'wrap',
  },
  scrollList: {
    flex: '1 1 auto',
    minHeight: 0,
    maxHeight: 'min(58dvh, 560px)',
    overflowY: 'auto',
    border: '2px solid rgba(212,165,88,0.24)',
    borderRadius: 'clamp(10px, 1.5vw, 14px)',
  },
opponentRail: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(clamp(168px, 20vw, 208px), clamp(168px, 20vw, 208px))',
    gap: 'clamp(6px, 1vw, 10px)',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 'clamp(4px, 0.8vw, 8px)',
    minHeight: 0,
    maxHeight: 'none',
    scrollbarWidth: 'none',          // Firefox ke liye
    msOverflowStyle: 'none',         // IE/Edge ke liye
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-x',
  },
  opponentCard: {
    position: 'relative',
    borderRadius: 'clamp(12px, 2vw, 18px)',
    border: '2px solid rgba(212,165,88,0.28)',
    background: 'linear-gradient(180deg, rgba(55,31,18,0.96), rgba(24,12,7,0.98))',
    padding: 'clamp(9px, 1vw, 12px)',
    display: 'grid',
    gap: 'clamp(6px, 0.8vw, 8px)',
    boxShadow: '0 16px 28px rgba(0,0,0,0.24)',
    minHeight: 'clamp(102px, 16vh, 122px)',
  },
  opponentCardTight: {
    gap: 8,
    padding: 10,
    minHeight: 110,
  },
  opponentCardBusy: {
    borderColor: 'rgba(215,177,106,0.45)',
    background: 'linear-gradient(180deg, rgba(60,41,26,0.96), rgba(30,18,11,0.98))',
  },
  opponentGlow: {
    position: 'absolute',
    inset: 'auto -30px -40px auto',
    width: 120,
    height: 120,
    background: 'radial-gradient(circle, rgba(240,195,106,0.18) 0%, rgba(240,195,106,0) 72%)',
  },
  avatar: {
    width: 'clamp(40px, 6vw, 50px)',
    height: 'clamp(40px, 6vw, 50px)',
    borderRadius: '50%',
    border: '2px solid #d7b16a',
    background: '#5a3118',
    color: '#f6e7c1',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 700,
    fontSize: 'clamp(1rem, 1.8vw, 1.3rem)',
    flexShrink: 0,
  },
  actionBtnCompact: {
    padding: '6px 8px',
    fontSize: '0.78rem',
    lineHeight: 1.1,
  },
  actionBtnDisabled: {
    cursor: 'not-allowed',
    opacity: 0.8,
    whiteSpace: 'nowrap',
    fontSize: '0.92rem',
    lineHeight: 1.2,
    letterSpacing: 0.2,
  },
  railControls: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  railButton: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '2px solid #d7b16a',
    background: '#3a2314',
    color: '#f6e7c1',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1,
    display: 'grid',
    placeItems: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10, 5, 3, 0.36)',
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    zIndex: 30,
  },
  requestModal: {
    width: 'min(92vw, 460px)',
    borderRadius: 18,
    border: '2px solid rgba(215,177,106,0.55)',
    background: 'linear-gradient(180deg, rgba(71,43,23,0.98), rgba(28,15,9,0.98))',
    boxShadow: '0 24px 60px rgba(0,0,0,0.42)',
    padding: '18px 16px',
    color: '#f6e7c1',
    display: 'grid',
    gap: 14,
  },
  requestModalTitle: {
    fontSize: '1.25rem',
    fontWeight: 800,
    color: '#ffd48a',
  },
  requestModalText: {
    fontSize: '0.98rem',
    lineHeight: 1.5,
    color: '#f8ead0',
  },
  requestModalActions: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  infoCard: {
    flex: 1,
    border: '2px solid rgba(212,165,88,0.28)',
    borderRadius: 'clamp(8px, 1.5vw, 14px)',
    padding: 'clamp(12px, 1.5vw, 18px)',
    background: 'rgba(58,35,20,0.5)',
    color: '#f6e7c1',
    overflowY: 'auto',
  },
  infoTitle: {
    fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
    fontWeight: 800,
    marginBottom: 'clamp(8px, 1.5vw, 12px)',
    color: '#ffffff',
  },
  infoLine: {
    color: '#ffffff',
    marginBottom: 'clamp(4px, 1vw, 8px)',
    fontSize: 'clamp(0.85rem, 1.3vw, 1rem)',
    lineHeight: 1.5,
  },
};

function menuBtn(active, compact = false) {
  return {
    width: compact ? 'auto' : '100%',
    minWidth: compact ? 0 : 'auto',
    border: '2px solid #d7b16a',
    background: active ? 'linear-gradient(135deg, #8a5a2b, #6a4820)' : '#3a2314',
    color: '#ffffff',
    padding: compact ? '8px 12px' : 'clamp(8px, 1vw, 12px) clamp(10px, 1.5vw, 14px)',
    borderRadius: 'clamp(8px, 1vw, 12px)',
    cursor: 'pointer',
    fontWeight: 700,
    textAlign: compact ? 'center' : 'left',
    flexShrink: 0,
    fontSize: compact ? '0.92rem' : 'clamp(0.85rem, 1.3vw, 1rem)',
    transition: 'all 200ms ease',
  };
}

function actionBtn(bg) {
  return {
    border: '2px solid #d7b16a',
    background: bg,
    color: '#f6e7c1',
    padding: 'clamp(7px, 1vw, 11px) clamp(10px, 1.5vw, 16px)',
    borderRadius: 'clamp(6px, 1vw, 10px)',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 'clamp(0.85rem, 1.3vw, 1rem)',
    transition: 'all 150ms ease',
  };
}
